import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteInfo } from '@/mocks/movies';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9niugkit35ii3jdlrsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '7777影院',
    url: 'https://www.7777yingyuan.com/',
    description: siteInfo.description,
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteInfo.phone,
      email: siteInfo.email,
      contactType: 'customer service',
    },
    address: { '@type': 'PostalAddress', addressLocality: '北京', addressCountry: 'CN' },
  };

  const jsonLdFAQ = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: siteInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLdOrg)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdFAQ)}</script>
      <Navbar />

      <main className="pt-20 min-h-screen bg-background-50">
        {/* Page Header */}
        <div className="bg-background-100 py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="py-2 mb-4">
              <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">关于7777影院</h1>
            <p className="text-sm text-foreground-500 max-w-2xl">{siteInfo.description}</p>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
          {/* Brand Intro */}
          <section className="py-12 md:py-16">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <h2 className="text-2xl font-heading font-bold text-foreground-50 mb-4">品牌介绍</h2>
                <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                  7777影院（又称777777影院）成立于2020年，是国内领先的免费高清影视在线观看平台。自成立以来，我们始终秉承"让每个人都能享受高品质影视内容"的使命，致力于为广大影迷提供最新、最全、最流畅的在线观影体验。
                </p>
                <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                  我们的团队汇聚了来自互联网、影视、技术等多个领域的专业人才，通过先进的视频编码技术和全球CDN加速网络，确保每一部影片都能以最佳画质流畅播放。无论您是在家中使用电脑，还是在路上用手机观影，7777影院都能为您提供影院级的视听享受。
                </p>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  作为国内影视资源最丰富的平台之一，7777影院已收录超过10万部电影、电视剧、综艺和动漫资源，并与多家内容提供商建立了长期合作关系。我们坚持每日更新，确保您永远不会错过任何一部好片。
                </p>
              </div>
              <div className="shrink-0 w-full lg:w-[400px]">
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/50">
                  <div className="grid grid-cols-2 gap-4">
                    {siteInfo.stats.map((stat) => (
                      <div key={stat.label} className="text-center p-4 bg-background-50 rounded-lg">
                        <div className="text-2xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                        <div className="text-xs text-foreground-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-12 md:py-16 bg-background-100 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="text-2xl font-heading font-bold text-foreground-50 mb-2">品牌口碑</h2>
              <p className="text-sm text-foreground-500 mb-8">来自千万用户的真实评价与信赖</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 rounded-lg p-6 border border-background-200/50">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    "7777影院是我用过最流畅的免费观影平台，画质清晰，更新及时，强烈推荐给所有追剧的朋友！"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary-500/20 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-200">追剧达人小王</p>
                      <p className="text-[10px] text-foreground-600">使用3年</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-lg p-6 border border-background-200/50">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    "资源太全了！我想看的冷门老片都能找到，而且真的不卡顿不缓冲，观影体验一级棒。"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <i className="ri-user-line text-primary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-200">电影发烧友老李</p>
                      <p className="text-[10px] text-foreground-600">使用5年</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-lg p-6 border border-background-200/50">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    "自从发现了7777影院，再也不用到处找资源了。手机电脑都能看，通勤路上也能追剧，太方便了。"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center">
                      <i className="ri-user-line text-accent-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-200">职场精英小陈</p>
                      <p className="text-[10px] text-foreground-600">使用2年</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="py-12 md:py-16">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <h2 className="text-2xl font-heading font-bold text-foreground-50 mb-2">联系我们</h2>
                <p className="text-sm text-foreground-500 mb-6">
                  如有任何问题、建议或合作意向，欢迎随时与我们联系。我们的客服团队将在24小时内回复您。
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-600">电子邮箱</p>
                      <p className="text-sm text-foreground-200">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-600">客服电话</p>
                      <p className="text-sm text-foreground-200">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-600">公司地址</p>
                      <p className="text-sm text-foreground-200">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="flex-1">
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/50">
                  <h3 className="text-lg font-semibold text-foreground-100 mb-4">发送消息</h3>

                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 mb-3">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-sm font-medium text-foreground-200 mb-1">消息已发送成功！</p>
                      <p className="text-xs text-foreground-500">感谢您的来信，我们将在24小时内回复</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                      <div>
                        <label htmlFor="contact-name" className="block text-xs text-foreground-500 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          required
                          className="w-full bg-background-50 border border-background-200/50 rounded-lg px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs text-foreground-500 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          required
                          className="w-full bg-background-50 border border-background-200/50 rounded-lg px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div className="honeypot-field">
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs text-foreground-500 mb-1.5">留言</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full bg-background-50 border border-background-200/50 rounded-lg px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        />
                      </div>
                      {formStatus === 'error' && formError && (
                        <p className="text-xs text-accent-500">{formError}</p>
                      )}
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-background-950 font-bold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
                      >
                        {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-heading font-bold text-foreground-50 mb-2">常见问题</h2>
              <p className="text-sm text-foreground-500">关于7777影院的常见疑问解答</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {siteInfo.faqs.map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-lg border border-background-200/50 overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer text-sm font-medium text-foreground-200 list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}